/**
 * AudiobookForm - Reusable form component for creating and editing audiobooks
 *
 * Supports both create and edit modes with author/reader/tag selectors
 */
package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.admin.ui.components.LabelSelector
import com.castly.shared.dto.AudiobookDto
import com.castly.shared.dto.AuthorDto
import com.castly.shared.dto.LabelDto
import com.castly.shared.dto.ReaderDto
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class AudiobookFormData(
    val isbn: String,
    val title: String,
    val subtitle: String?,
    val titleEnglish: String?,
    val subtitleEnglish: String?,
    val authorId: Int?,
    val readerId: Int?,
    val publisher: String?,
    val language: String?,
    val originalLanguage: String?,
    val description: String?,
    val copyrightYear: Int?,
    val releaseDate: String?,
    val tagIds: List<String> = emptyList(),
    val labelId: Int? = null
)

@Composable
fun AudiobookForm(
    audiobook: AudiobookDto? = null,
    authors: List<AuthorDto> = emptyList(),
    readers: List<ReaderDto> = emptyList(),
    labels: List<LabelDto> = emptyList(),
    userLabelId: Int? = null,
    isLabelEditor: Boolean = false,
    isLoading: Boolean = false,
    errorMessage: String? = null,
    onSave: (AudiobookFormData) -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    var isbn by remember(audiobook) { mutableStateOf(audiobook?.isbn ?: "") }
    var title by remember(audiobook) { mutableStateOf(audiobook?.title ?: "") }
    var subtitle by remember(audiobook) { mutableStateOf(audiobook?.subtitle ?: "") }
    var titleEnglish by remember(audiobook) { mutableStateOf(audiobook?.titleEnglish ?: "") }
    var subtitleEnglish by remember(audiobook) { mutableStateOf(audiobook?.subtitleEnglish ?: "") }
    var selectedAuthorId by remember(audiobook) { mutableStateOf(audiobook?.authorId?.toString() ?: "") }
    var selectedReaderId by remember(audiobook) { mutableStateOf(audiobook?.readerId?.toString() ?: "") }
    var publisher by remember(audiobook) { mutableStateOf(audiobook?.publisher ?: "") }
    var language by remember(audiobook) { mutableStateOf(audiobook?.language ?: "ar") }
    var originalLanguage by remember(audiobook) { mutableStateOf(audiobook?.originalLanguage ?: "") }
    var description by remember(audiobook) { mutableStateOf(audiobook?.description ?: "") }
    var copyrightYear by remember(audiobook) { mutableStateOf(audiobook?.copyrightYear?.toString() ?: "") }
    var releaseDate by remember(audiobook) { mutableStateOf(audiobook?.releaseDate ?: "") }
    var selectedLabelId by remember(audiobook) { mutableStateOf<Int?>(audiobook?.labelId ?: if (isLabelEditor) userLabelId else null) }
    val isEditMode = audiobook != null
    val isValid = title.isNotBlank() && (isEditMode || selectedLabelId != null)

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(800.px)
            themeTransition()
        }
    }) {
        H2({
            style {
                margin(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
            }
        }) {
            Text(if (isEditMode) "Edit Audiobook" else "Create Audiobook")
        }

        // Error message
        errorMessage?.let { error ->
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.md)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Text("Error")
                }
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.danger))
                    }
                }) {
                    Text(error)
                }
            }
        }

        // Label selector (required for new audiobooks)
        if (!isEditMode && labels.isNotEmpty()) {
            Div({
                style { marginBottom(Spacing.lg) }
            }) {
                LabelSelector(
                    labels = labels,
                    selectedLabelId = selectedLabelId,
                    onLabelSelected = { selectedLabelId = it },
                    readOnly = isLabelEditor,
                    userLabelId = userLabelId
                )
            }
        }

        // Two-column layout for form fields
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "1fr 1fr")
                gap(Spacing.lg)
            }
        }) {
            // ISBN field (full width on create, disabled on edit)
            Div({
                style {
                    property("grid-column", if (!isEditMode) "1 / -1" else "auto")
                }
            }) {
                FormField(
                    label = "ISBN",
                    required = true
                ) {
                    Input(InputType.Text) {
                        value(isbn)
                        onInput { isbn = it.value }
                        attr("placeholder", "Enter ISBN")
                        if (isLoading || isEditMode) disabled()
                        style {
                            width(100.percent)
                            padding(10.px, Spacing.md)
                            fontSize(Typography.base)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            property("outline", "none")
                            property("box-sizing", "border-box")
                            if (isLoading || isEditMode) {
                                backgroundColor(Color(colors.surfaceHover))
                                cursor("not-allowed")
                            }
                        }
                    }
                }
            }

            // Title (Arabic) - full width
            Div({
                style {
                    property("grid-column", "1 / -1")
                }
            }) {
                FormField(
                    label = "Title (Arabic)",
                    required = true
                ) {
                    Input(InputType.Text) {
                        value(title)
                        onInput { title = it.value }
                        attr("placeholder", "Enter Arabic title")
                        if (isLoading) disabled()
                        style {
                            width(100.percent)
                            padding(10.px, Spacing.md)
                            fontSize(Typography.base)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            property("outline", "none")
                            property("box-sizing", "border-box")
                            if (isLoading) {
                                backgroundColor(Color(colors.surfaceHover))
                                cursor("not-allowed")
                            }
                        }
                    }
                }
            }

            // Subtitle (Arabic)
            FormField(label = "Subtitle (Arabic)") {
                Input(InputType.Text) {
                    value(subtitle)
                    onInput { subtitle = it.value }
                    attr("placeholder", "Optional")
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Title (English)
            FormField(label = "Title (English)") {
                Input(InputType.Text) {
                    value(titleEnglish)
                    onInput { titleEnglish = it.value }
                    attr("placeholder", "Optional")
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Subtitle (English)
            FormField(label = "Subtitle (English)") {
                Input(InputType.Text) {
                    value(subtitleEnglish)
                    onInput { subtitleEnglish = it.value }
                    attr("placeholder", "Optional")
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Author selector
            FormField(
                label = "Author"
            ) {
                key(authors.size) {
                    Select({
                        onInput { selectedAuthorId = it.value ?: "" }
                        if (isLoading) disabled()
                        style {
                            width(100.percent)
                            padding(10.px, Spacing.md)
                            fontSize(Typography.base)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            property("outline", "none")
                            property("box-sizing", "border-box")
                            backgroundColor(Color(colors.surface))
                            color(Color(colors.text))
                            if (isLoading) {
                                backgroundColor(Color(colors.surfaceHover))
                                cursor("not-allowed")
                            }
                        }
                    }) {
                        Option("", {
                            if (selectedAuthorId.isBlank()) selected()
                        }) {
                            Text(if (authors.isEmpty()) "Loading authors..." else "Select an author (optional)")
                        }
                        authors.forEach { author ->
                            Option(author.id.toString(), {
                                if (selectedAuthorId == author.id.toString()) selected()
                            }) {
                                Text(author.name)
                            }
                        }
                    }
                }
            }

            // Reader selector
            FormField(label = "Reader/Narrator") {
                key(readers.size) {
                    Select({
                        onInput { selectedReaderId = it.value ?: "" }
                        if (isLoading) disabled()
                        style {
                            width(100.percent)
                            padding(10.px, Spacing.md)
                            fontSize(Typography.base)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            property("outline", "none")
                            property("box-sizing", "border-box")
                            backgroundColor(Color(colors.surface))
                            color(Color(colors.text))
                            if (isLoading) {
                                backgroundColor(Color(colors.surfaceHover))
                                cursor("not-allowed")
                            }
                        }
                    }) {
                        Option("", {
                            if (selectedReaderId.isBlank()) selected()
                        }) {
                            Text(if (readers.isEmpty()) "Loading readers..." else "Select a reader (optional)")
                        }
                        readers.forEach { reader ->
                            Option(reader.id.toString(), {
                                if (selectedReaderId == reader.id.toString()) selected()
                            }) {
                                Text(reader.name)
                            }
                        }
                    }
                }
            }

            // Publisher
            FormField(label = "Publisher") {
                Input(InputType.Text) {
                    value(publisher)
                    onInput { publisher = it.value }
                    attr("placeholder", "Optional")
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Language
            FormField(label = "Language") {
                Select({
                    onInput { language = it.value ?: "ar" }
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }) {
                    Option("ar", { if (language == "ar") selected() }) { Text("Arabic") }
                    Option("en", { if (language == "en") selected() }) { Text("English") }
                    Option("fr", { if (language == "fr") selected() }) { Text("French") }
                    Option("es", { if (language == "es") selected() }) { Text("Spanish") }
                    Option("de", { if (language == "de") selected() }) { Text("German") }
                }
            }

            // Copyright Year
            FormField(label = "Copyright Year") {
                Input(InputType.Text) {
                    value(copyrightYear)
                    onInput { copyrightYear = it.value }
                    attr("placeholder", "YYYY")
                    attr("min", "1900")
                    attr("max", "2100")
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Release Date
            FormField(label = "Release Date") {
                Input(InputType.Date) {
                    value(releaseDate)
                    onInput { releaseDate = it.value }
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }
        }

        // Description field - full width
        Div({
            style {
                marginTop(Spacing.lg)
            }
        }) {
            FormField(label = "Description") {
                TextArea {
                    value(description)
                    onInput { description = it.value }
                    attr("placeholder", "Enter audiobook description (optional)")
                    rows(4)
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        property("resize", "vertical")
                        fontFamily("inherit")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }
        }


        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.md)
                justifyContent(JustifyContent.FlexEnd)
                marginTop(Spacing.xxl)
            }
        }) {
            Button({
                onClick { onCancel() }
                if (isLoading) disabled()
                style {
                    padding(10.px, Spacing.xl)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.text))
                    backgroundColor(Color(colors.surface))
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    cursor(if (isLoading) "not-allowed" else "pointer")
                    property("transition", "all 0.2s")
                }
            }) {
                Text("Cancel")
            }

            Button({
                onClick {
                    if (isValid && !isLoading) {
                        onSave(
                            AudiobookFormData(
                                isbn = isbn,
                                title = title,
                                subtitle = subtitle.takeIf { it.isNotBlank() },
                                titleEnglish = titleEnglish.takeIf { it.isNotBlank() },
                                subtitleEnglish = subtitleEnglish.takeIf { it.isNotBlank() },
                                authorId = selectedAuthorId.takeIf { it.isNotBlank() }?.toIntOrNull(),
                                readerId = selectedReaderId.takeIf { it.isNotBlank() }?.toInt(),
                                publisher = publisher.takeIf { it.isNotBlank() },
                                language = language.takeIf { it.isNotBlank() },
                                originalLanguage = originalLanguage.takeIf { it.isNotBlank() },
                                description = description.takeIf { it.isNotBlank() },
                                copyrightYear = copyrightYear.takeIf { it.isNotBlank() }?.toIntOrNull(),
                                releaseDate = releaseDate.takeIf { it.isNotBlank() },
                                labelId = selectedLabelId
                            )
                        )
                    }
                }
                if (!isValid || isLoading) disabled()
                style {
                    padding(10.px, Spacing.xl)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.surface))
                    backgroundColor(if (!isValid || isLoading) Color(colors.textMuted) else Color(colors.primary))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor(if (!isValid || isLoading) "not-allowed" else "pointer")
                    property("transition", "all 0.2s")
                }
            }) {
                Text(when {
                    isLoading -> "Saving..."
                    isEditMode -> "Update Audiobook"
                    else -> "Create Audiobook"
                })
            }
        }
    }
}

@Composable
private fun FormField(
    label: String,
    required: Boolean = false,
    content: @Composable () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style {
            marginBottom(Spacing.lg)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Block)
                fontSize(Typography.base)
                fontWeight("500")
                color(Color(colors.text))
                marginBottom(6.px)
            }
        }) {
            Text(label)
            if (required) {
                Span({
                    style {
                        color(Color(colors.danger))
                        marginLeft(2.px)
                    }
                }) {
                    Text("*")
                }
            }
        }

        content()
    }
}
